/**
 * Provider Adapters
 *
 * Maps the function modules (*-browser.ts) onto the AIProvider interface.
 * Gemini, OpenAI, and the free test provider are self-contained classes in
 * their own files.
 */

import type { AIProvider } from "./provider-interface";

import * as openrouter from "./openrouter-browser";
import * as chutes from "./chutes-browser";
import * as manual from "./manual-browser";
import * as wavespeed from "./wavespeed-browser";
import * as cometapi from "./cometapi-browser";

export const openRouterProvider: AIProvider = {
  validateApiKey: openrouter.validateOpenRouterKey,
  generateCharacterProfile: (prompts, config, _worldDocument, opts) =>
    openrouter.generateCharacterProfileOpenRouter(prompts, config, opts),
  generateChatExportFields: openrouter.generateChatExportFieldsOpenRouter,
  generateImagePlanPrompts: openrouter.generateImagePlanPromptsOpenRouter,
  rewriteCharacterField: openrouter.rewriteCharacterFieldOpenRouter,
  editCharacter: openrouter.editCharacterOpenRouter,
  generateRoleplayReply: openrouter.generateRoleplayReplyOpenRouter,
  generateImage: openrouter.generateImageOpenRouter,
  generateImageEdit: openrouter.generateImageEditOpenRouter,
};

export const chutesProvider: AIProvider = {
  validateApiKey: (config) =>
    chutes.validateChutesKey({
      apiKey: config.apiKey,
      model: config.model || '',
      customEndpoint: config.customEndpoint,
    }),
  generateCharacterProfile: (prompts, config, _worldDocument, opts) =>
    chutes.generateCharacterProfileChutes(prompts, config, opts),
  generateChatExportFields: chutes.generateChatExportFieldsChutes,
  generateImagePlanPrompts: chutes.generateImagePlanPromptsChutes,
  rewriteCharacterField: chutes.rewriteCharacterFieldChutes,
  editCharacter: chutes.editCharacterChutes,
  generateRoleplayReply: chutes.generateRoleplayReplyChutes,
};

export const manualProvider: AIProvider = {
  validateApiKey: (config) =>
    manual.validateManualKey({
      apiKey: config.apiKey,
      model: config.model || '',
      customEndpoint: config.customEndpoint,
    }),
  generateCharacterProfile: (prompts, config, _worldDocument, opts) =>
    manual.generateCharacterProfileManual(prompts, config, opts),
  generateChatExportFields: manual.generateChatExportFieldsManual,
  generateImagePlanPrompts: manual.generateImagePlanPromptsManual,
  rewriteCharacterField: manual.rewriteCharacterFieldManual,
  editCharacter: manual.editCharacterManual,
  generateRoleplayReply: manual.generateRoleplayReplyManual,
  generateImage: manual.generateImageManual,
  generateImageEdit: manual.generateImageEditManual,
};

export const waveSpeedProvider: AIProvider = {
  validateApiKey: (config) =>
    wavespeed.validateWavespeedKey({
      apiKey: config.apiKey,
      model: config.model || '',
    }),
  generateImage: async (prompt, config, opts) => ({
    value: await wavespeed.generateImageWavespeed(prompt, config, opts),
  }),
  generateImageEdit: async (referenceImage, prompt, config, opts) => ({
    value: await wavespeed.generateImageEditWavespeed(referenceImage, prompt, config, opts),
  }),
};

export const cometAPIProvider: AIProvider = {
  validateApiKey: (config) =>
    cometapi.validateCometAPIKey({
      apiKey: config.apiKey,
      model: config.model || '',
      customEndpoint: config.customEndpoint,
    }),
  generateImage: async (prompt, config, opts) => ({
    value: await cometapi.generateImageCometAPI(prompt, config, opts),
  }),
};
